const flattenQuestionCitations = (citation) => {
  if (!citation) return [];

  const rows = [];

  if (citation.fact_sheet) {
    rows.push({
      ref: `DOL Fact Sheet #${citation.fact_sheet.ref}`,
      url: citation.fact_sheet.url,
    });
  }

  if (citation.cfr) {
    rows.push({ ref: citation.cfr.ref, url: citation.cfr.url });
  }

  if (citation.cfr_also) {
    rows.push({ ref: citation.cfr_also.ref, url: citation.cfr_also.url });
  }

  return rows;
};

const makeQuestion = (id, question, hint, citation) => ({
  id,
  question,
  hint: hint ?? null,
  citations: flattenQuestionCitations(citation),
});

export const FLSA_SECTIONS = [
  {
    id: 'eligibility',
    title: 'Eligibility',
    isExemption: false,
    description:
      'Before reviewing exemption categories, confirm that this role is eligible for consideration under the FLSA white-collar exemptions.',
    citation: {
      flsa: '§ 13(a)(1)',
      fact_sheet: { ref: '17A', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17a-overtime' },
      cfr: { ref: '29 C.F.R. Part 541', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541' },
    },
    groups: [
      {
        label: null,
        questions: [
          makeQuestion(
            '541_3a',
            'This role is a white-collar office or non-manual position.',
            'The white-collar exemptions under FLSA § 13(a)(1) do not apply to roles primarily involving manual labor, physical skill, or repetitive operations (such as carpenters, electricians, mechanics, or construction workers) regardless of pay level.',
            {
              fact_sheet: { ref: '17I', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17i-overtime-blue-collar' },
              cfr: { ref: '29 C.F.R. § 541.3', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.3' },
            },
          ),
          makeQuestion(
            '541_3b',
            'This role is not a first responder or law enforcement position.',
            'Police officers, firefighters, paramedics, EMTs, correctional officers, and investigators performing law enforcement work are excluded from these exemptions regardless of rank or pay.',
            {
              fact_sheet: { ref: '17J', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17j-overtime-first-responders' },
              cfr: { ref: '29 C.F.R. § 541.3(b)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.3' },
            },
          ),
        ],
      },
    ],
  },
  {
    id: 'executive',
    title: 'Executive',
    isExemption: true,
    description: 'All four statements must be true for this exemption to apply.',
    citation: {
      flsa: '§ 13(a)(1)',
      fact_sheet: { ref: '17B', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17b-overtime-executive' },
      cfr: { ref: '29 C.F.R. § 541.100', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-B/section-541.100' },
    },
    groups: [
      {
        label: 'Compensation',
        questions: [
          makeQuestion(
            '541_600',
            'This role is compensated on a salary or fee basis at or above $684 per week.',
            'Nondiscretionary bonuses and commissions paid at least annually may satisfy up to 10% of this threshold.',
            {
              fact_sheet: { ref: '17G', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17g-overtime-salary' },
              cfr: { ref: '29 C.F.R. § 541.600', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.600' },
            },
          ),
        ],
      },
      {
        label: 'Duties',
        questions: [
          makeQuestion(
            '541_100a2',
            "This role's primary duty is management of the enterprise, or a recognized department or subdivision.",
            'Primary duty means the principal, main, or most important duty. It is not merely an occasional responsibility.',
            { cfr: { ref: '29 C.F.R. § 541.100(a)(2)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-B/section-541.100' } },
          ),
          makeQuestion(
            '541_100a3',
            'This role customarily and regularly directs the work of at least two full-time employees or their equivalent.',
            'Part-time employees count if their combined hours equal two full-time positions.',
            { cfr: { ref: '29 C.F.R. § 541.100(a)(3)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-B/section-541.100' } },
          ),
          makeQuestion(
            '541_100a4',
            'This role has the authority to hire or fire, or its recommendations on hiring, firing, advancement, or promotion are given particular weight.',
            '"Particular weight" means recommendations are genuinely influential - not routinely overridden or treated as a formality.',
            { cfr: { ref: '29 C.F.R. § 541.100(a)(4)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-B/section-541.100' } },
          ),
        ],
      },
    ],
  },
  {
    id: 'administrative',
    title: 'Administrative',
    isExemption: true,
    description: 'All three statements must be true for this exemption to apply.',
    citation: {
      flsa: '§ 13(a)(1)',
      fact_sheet: { ref: '17C', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17c-overtime-administrative' },
      cfr: { ref: '29 C.F.R. § 541.200', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-C/section-541.200' },
    },
    groups: [
      {
        label: 'Compensation',
        questions: [
          makeQuestion(
            '541_600',
            'This role is compensated on a salary or fee basis at or above $684 per week.',
            'Nondiscretionary bonuses and commissions paid at least annually may satisfy up to 10% of this threshold.',
            {
              fact_sheet: { ref: '17G', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17g-overtime-salary' },
              cfr: { ref: '29 C.F.R. § 541.600', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.600' },
            },
          ),
        ],
      },
      {
        label: 'Duties',
        questions: [
          makeQuestion(
            '541_200a2',
            "This role's primary duty is the performance of office or non-manual work directly related to the management or general business operations of the employer or its customers.",
            'Covers functions like finance, HR, legal, compliance, and IT, as opposed to production or front-line service delivery.',
            { cfr: { ref: '29 C.F.R. § 541.200(a)(2)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-C/section-541.200' } },
          ),
          makeQuestion(
            '541_200a3',
            "This role's primary duty includes the exercise of discretion and independent judgement with respect to matters of significance.",
            'Authority to make independent choices free from immediate direction. It is not simply applying established procedures to routine situations.',
            {
              cfr: { ref: '29 C.F.R. § 541.200(a)(3)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-C/section-541.200' },
              cfr_also: { ref: '29 C.F.R. § 541.202', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-C/section-541.202' },
            },
          ),
        ],
      },
    ],
  },
  {
    id: 'learned-professional',
    title: 'Learned Professional',
    isExemption: true,
    description:
      'All four statements must be true for this path to apply. Independent of the creative professional path, either satisfies the professional exemption on its own.',
    citation: {
      flsa: '§ 13(a)(1)',
      fact_sheet: { ref: '17D', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17d-overtime-professional' },
      cfr: { ref: '29 C.F.R. § 541.301', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-D/section-541.301' },
    },
    groups: [
      {
        label: 'Compensation',
        questions: [
          makeQuestion(
            '541_600',
            'This role is compensated on a salary or fee basis at or above $684 per week.',
            'Nondiscretionary bonuses and commissions paid at least annually may satisfy up to 10% of this threshold.',
            {
              fact_sheet: { ref: '17G', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17g-overtime-salary' },
              cfr: { ref: '29 C.F.R. § 541.600', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.600' },
            },
          ),
        ],
      },
      {
        label: 'Duties',
        questions: [
          makeQuestion(
            '541_301b',
            "This role's primary duty requires advanced knowledge, as demonstrated by work that is predominantly intellectual in character and that includes the consistent exercise of discretion and judgement.",
            'This is distinguished from routine mental, manual, mechanical, or physical work.',
            { cfr: { ref: '29 C.F.R. § 541.301(b)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-D/section-541.301' } },
          ),
          makeQuestion(
            '541_301c',
            'The advanced knowledge required by this role is in a recognized field of science or learning.',
            'Examples include law, medicine, theology, accounting, actuarial computation, engineering, architecture, teaching, or the physical, chemical, or biological sciences but also include other similar occupations that have a recognized professional status.',
            { cfr: { ref: '29 C.F.R. § 541.301(c)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-D/section-541.301' } },
          ),
          makeQuestion(
            '541_301d',
            "This role's primary duty requires knowledge customarily acquired by a prolonged course of specialized intellectual instruction.",
            'A degree program, professional certification, or equivalent. It is not primarily through on-the-job training or experience alone.',
            { cfr: { ref: '29 C.F.R. § 541.301(d)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-D/section-541.301' } },
          ),
        ],
      },
    ],
  },
  {
    id: 'creative-professional',
    title: 'Creative Professional',
    isExemption: true,
    description:
      'Both statements must be true for this path to apply. Independent of the learned professional path, either satisfies the professional exemption on its own.',
    citation: {
      flsa: '§ 13(a)(1)',
      fact_sheet: { ref: '17D', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17d-overtime-professional' },
      cfr: { ref: '29 C.F.R. § 541.302', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-D/section-541.302' },
    },
    groups: [
      {
        label: 'Compensation',
        questions: [
          makeQuestion(
            '541_600',
            'This role is compensated on a salary or fee basis at or above $684 per week.',
            'Nondiscretionary bonuses and commissions paid at least annually may satisfy up to 10% of this threshold.',
            {
              fact_sheet: { ref: '17G', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17g-overtime-salary' },
              cfr: { ref: '29 C.F.R. § 541.600', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.600' },
            },
          ),
        ],
      },
      {
        label: 'Duties',
        questions: [
          makeQuestion(
            '541_302a',
            "This role's primary duty requires invention, imagination, originality, or talent in a recognized field of artistic or creative endeavor.",
            "The creative output must depend on the employee's unique skill. It is not the result of following a formula, template, or standardized process.",
            { cfr: { ref: '29 C.F.R. § 541.302(a)–(b)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-D/section-541.302' } },
          ),
        ],
      },
    ],
  },
  {
    id: 'computer',
    title: 'Computer',
    isExemption: true,
    description:
      'All statements must be true. Unlike other categories, this exemption can be satisfied on an hourly basis.',
    citation: {
      flsa: '§ 13(a)(1) and § 13(a)(17)',
      fact_sheet: { ref: '17E', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17e-overtime-computer' },
      cfr: { ref: '29 C.F.R. § 541.400', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-E/section-541.400' },
    },
    groups: [
      {
        label: 'Compensation',
        questions: [
          makeQuestion(
            '541_400b',
            'This role is compensated on a salary or fee basis at or above $684 per week, or on an hourly basis at or above $27.63 per hour.',
            'The hourly alternative is unique to this exemption.',
            { cfr: { ref: '29 C.F.R. § 541.400(b)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-E/section-541.400' } },
          ),
        ],
      },
      {
        label: 'Duties',
        questions: [
          makeQuestion(
            '541_400a',
            'This role is employed as a computer systems analyst, programmer, software engineer, or similarly skilled worker in the computer field.',
            'Help desk support, data entry, and general hardware roles typically do not qualify.',
            { cfr: { ref: '29 C.F.R. § 541.400(a)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-E/section-541.400' } },
          ),
          makeQuestion(
            '541_400b1_4',
            "This role's primary duty consists of systems analysis, design or development of computer systems or programs, or modification of operating system programs, or a combination requiring the same level of skill.",
            null,
            { cfr: { ref: '29 C.F.R. § 541.400(b)(1)–(4)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-E/section-541.400' } },
          ),
        ],
      },
    ],
  },
  {
    id: 'outside-sales',
    title: 'Outside Sales',
    isExemption: true,
    description: 'Both statements must be true. There is no salary requirement for this exemption.',
    citation: {
      flsa: '§ 13(a)(1)',
      fact_sheet: { ref: '17F', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17f-overtime-outside-sales' },
      cfr: { ref: '29 C.F.R. § 541.500', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-F/section-541.500' },
    },
    groups: [
      {
        label: null,
        questions: [
          makeQuestion(
            '541_500a1',
            "This role's primary duty is making sales or obtaining orders or contracts for services or facilities for which a consideration will be paid.",
            null,
            { cfr: { ref: '29 C.F.R. § 541.500(a)(1)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-F/section-541.500' } },
          ),
          makeQuestion(
            '541_500a2',
            "This role is customarily and regularly engaged away from the employer's place of business.",
            "Inside sales roles, or those who primarily sell by phone or remotely from an employer's location, do not meet this criterion.",
            { cfr: { ref: '29 C.F.R. § 541.500(a)(2)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-F/section-541.500' } },
          ),
        ],
      },
    ],
  },
  {
    id: 'highly-compensated',
    title: 'Highly Compensated',
    isExemption: true,
    description:
      'All three statements must be checked. This is an alternative path that does not require satisfying the full duties tests of other categories.',
    citation: {
      flsa: '§ 13(a)(1)',
      fact_sheet: { ref: '17H', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17h-overtime-highly-compensated' },
      cfr: { ref: '29 C.F.R. § 541.601', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.601' },
    },
    groups: [
      {
        label: 'Compensation',
        questions: [
          makeQuestion(
            '541_601b',
            "This role's total annual compensation includes at least $684 per week paid on a salary or fee basis.",
            'Required even where total compensation well exceeds the $107,432 threshold',
            { cfr: { ref: '29 C.F.R. § 541.601(b)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.601' } },
          ),
          makeQuestion(
            '541_601a',
            "This role's total annual compensation is at or above $107,432.",
            'May include commissions and nondiscretionary bonuses but not board, lodging, or other facilities.',
            { cfr: { ref: '29 C.F.R. § 541.601(a)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.601' } },
          ),
        ],
      },
      {
        label: 'Duties',
        questions: [
          makeQuestion(
            '541_601d',
            'This role performs office or non-manual work and customarily and regularly performs at least one of the duties of an exempt executive, administrative, or professional employee.',
            'Only one qualifying duty from any of the three categories is required. The full duties tests do not apply here.',
            { cfr: { ref: '29 C.F.R. § 541.601(a) and (d)', url: 'https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.601' } },
          ),
        ],
      },
    ],
  },
];

export const FLSA_SECTIONS_2024 = [
  {
    id: 'eligibility',
    title: 'Eligibility',
    isExemption: false,
    description:
      'Before reviewing exemption categories, confirm that this role is eligible for consideration under the FLSA white-collar exemptions.',
    citation: {
      flsa: '§ 13(a)(1)',
      fact_sheet: { ref: '17A', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17a-overtime' },
      cfr: { ref: '29 C.F.R. Part 541', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541' },
    },
    groups: [
      {
        label: null,
        questions: [
          makeQuestion(
            '541_3a',
            'This role is a white-collar office or non-manual position.',
            'The white-collar exemptions under FLSA § 13(a)(1) do not apply to roles primarily involving manual labor, physical skill, or repetitive operations (such as carpenters, electricians, mechanics, or construction workers) regardless of pay level.',
            {
              fact_sheet: { ref: '17I', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17i-overtime-blue-collar' },
              cfr: { ref: '29 C.F.R. § 541.3', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.3' },
            },
          ),
          makeQuestion(
            '541_3b',
            'This role is not a first responder or law enforcement position.',
            'Police officers, firefighters, paramedics, EMTs, correctional officers, and investigators performing law enforcement work are excluded from these exemptions regardless of rank or pay.',
            {
              fact_sheet: { ref: '17J', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17j-overtime-first-responders' },
              cfr: { ref: '29 C.F.R. § 541.3(b)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.3' },
            },
          ),
        ],
      },
    ],
  },
  {
    id: 'executive',
    title: 'Executive',
    isExemption: true,
    description: 'All four statements must be true for this exemption to apply.',
    citation: {
      flsa: '§ 13(a)(1)',
      fact_sheet: { ref: '17B', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17b-overtime-executive' },
      cfr: { ref: '29 C.F.R. § 541.100', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-B/section-541.100' },
    },
    groups: [
      {
        label: 'Compensation',
        questions: [
          makeQuestion(
            '541_600',
            'This role is compensated on a salary or fee basis at or above $844 per week.',
            'Nondiscretionary bonuses and commissions paid at least annually may satisfy up to 10% of this threshold.',
            {
              fact_sheet: { ref: '17G', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17g-overtime-salary' },
              cfr: { ref: '29 C.F.R. § 541.600', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.600' },
            },
          ),
        ],
      },
      {
        label: 'Duties',
        questions: [
          makeQuestion(
            '541_100a2',
            "This role's primary duty is management of the enterprise, or a recognized department or subdivision.",
            'Primary duty means the principal, main, or most important duty. It is not merely an occasional responsibility.',
            { cfr: { ref: '29 C.F.R. § 541.100(a)(2)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-B/section-541.100' } },
          ),
          makeQuestion(
            '541_100a3',
            'This role customarily and regularly directs the work of at least two full-time employees or their equivalent.',
            'Part-time employees count if their combined hours equal two full-time positions.',
            { cfr: { ref: '29 C.F.R. § 541.100(a)(3)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-B/section-541.100' } },
          ),
          makeQuestion(
            '541_100a4',
            'This role has the authority to hire or fire, or its recommendations on hiring, firing, advancement, or promotion are given particular weight.',
            '"Particular weight" means recommendations are genuinely influential - not routinely overridden or treated as a formality.',
            { cfr: { ref: '29 C.F.R. § 541.100(a)(4)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-B/section-541.100' } },
          ),
        ],
      },
    ],
  },
  {
    id: 'administrative',
    title: 'Administrative',
    isExemption: true,
    description: 'All three statements must be true for this exemption to apply.',
    citation: {
      flsa: '§ 13(a)(1)',
      fact_sheet: { ref: '17C', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17c-overtime-administrative' },
      cfr: { ref: '29 C.F.R. § 541.200', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-C/section-541.200' },
    },
    groups: [
      {
        label: 'Compensation',
        questions: [
          makeQuestion(
            '541_600',
            'This role is compensated on a salary or fee basis at or above $844 per week.',
            'Nondiscretionary bonuses and commissions paid at least annually may satisfy up to 10% of this threshold.',
            {
              fact_sheet: { ref: '17G', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17g-overtime-salary' },
              cfr: { ref: '29 C.F.R. § 541.600', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.600' },
            },
          ),
        ],
      },
      {
        label: 'Duties',
        questions: [
          makeQuestion(
            '541_200a2',
            "This role's primary duty is the performance of office or non-manual work directly related to the management or general business operations of the employer or its customers.",
            'Covers functions like finance, HR, legal, compliance, and IT, as opposed to production or front-line service delivery.',
            { cfr: { ref: '29 C.F.R. § 541.200(a)(2)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-C/section-541.200' } },
          ),
          makeQuestion(
            '541_200a3',
            "This role's primary duty includes the exercise of discretion and independent judgement with respect to matters of significance.",
            'Authority to make independent choices free from immediate direction. It is not simply applying established procedures to routine situations.',
            {
              cfr: { ref: '29 C.F.R. § 541.200(a)(3)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-C/section-541.200' },
              cfr_also: { ref: '29 C.F.R. § 541.202', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-C/section-541.202' },
            },
          ),
        ],
      },
    ],
  },
  {
    id: 'learned-professional',
    title: 'Learned Professional',
    isExemption: true,
    description:
      'All three statements must be true for this path to apply. Independent of the creative professional path, either satisfies the professional exemption on its own.',
    citation: {
      flsa: '§ 13(a)(1)',
      fact_sheet: { ref: '17D', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17d-overtime-professional' },
      cfr: { ref: '29 C.F.R. § 541.301', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-D/section-541.301' },
    },
    groups: [
      {
        label: 'Compensation',
        questions: [
          makeQuestion(
            '541_600',
            'This role is compensated on a salary or fee basis at or above $844 per week.',
            'Nondiscretionary bonuses and commissions paid at least annually may satisfy up to 10% of this threshold.',
            {
              fact_sheet: { ref: '17G', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17g-overtime-salary' },
              cfr: { ref: '29 C.F.R. § 541.600', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.600' },
            },
          ),
        ],
      },
      {
        label: 'Duties',
        questions: [
          makeQuestion(
            '541_301b',
            "This role's primary duty requires advanced knowledge, as demonstrated by work that is predominantly intellectual in character and that includes the consistent exercise of discretion and judgement.",
            'This is distinguished from routine mental, manual, mechanical, or physical work.',
            { cfr: { ref: '29 C.F.R. § 541.301(b)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-D/section-541.301' } },
          ),
          makeQuestion(
            '541_301c',
            'The advanced knowledge required by this role is in a recognized field of science or learning.',
            'Examples include law, medicine, theology, accounting, actuarial computation, engineering, architecture, teaching, or the physical, chemical, or biological sciences but also include other similar occupations that have a recognized professional status.',
            { cfr: { ref: '29 C.F.R. § 541.301(c)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-D/section-541.301' } },
          ),
          makeQuestion(
            '541_301d',
            "This role's primary duty requires knowledge customarily acquired by a prolonged course of specialized intellectual instruction.",
            'A degree program, professional certification, or equivalent. It is not primarily through on-the-job training or experience alone.',
            { cfr: { ref: '29 C.F.R. § 541.301(d)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-D/section-541.301' } },
          ),
        ],
      },
    ],
  },
  {
    id: 'creative-professional',
    title: 'Creative Professional',
    isExemption: true,
    description:
      'Both statements must be true for this path to apply. Independent of the learned professional path, either satisfies the professional exemption on its own.',
    citation: {
      flsa: '§ 13(a)(1)',
      fact_sheet: { ref: '17D', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17d-overtime-professional' },
      cfr: { ref: '29 C.F.R. § 541.302', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-D/section-541.302' },
    },
    groups: [
      {
        label: 'Compensation',
        questions: [
          makeQuestion(
            '541_600',
            'This role is compensated on a salary or fee basis at or above $844 per week.',
            'Nondiscretionary bonuses and commissions paid at least annually may satisfy up to 10% of this threshold.',
            {
              fact_sheet: { ref: '17G', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17g-overtime-salary' },
              cfr: { ref: '29 C.F.R. § 541.600', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.600' },
            },
          ),
        ],
      },
      {
        label: 'Duties',
        questions: [
          makeQuestion(
            '541_302a',
            "This role's primary duty requires invention, imagination, originality, or talent in a recognized field of artistic or creative endeavor.",
            "The creative output must depend on the employee's unique skill. It is not the result of following a formula, template, or standardized process.",
            { cfr: { ref: '29 C.F.R. § 541.302(a)–(b)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-D/section-541.302' } },
          ),
        ],
      },
    ],
  },
  {
    id: 'computer',
    title: 'Computer',
    isExemption: true,
    description:
      'All statements must be true. Unlike other categories, this exemption can be satisfied on an hourly basis.',
    citation: {
      flsa: '§ 13(a)(1) and § 13(a)(17)',
      fact_sheet: { ref: '17E', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17e-overtime-computer' },
      cfr: { ref: '29 C.F.R. § 541.400', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-E/section-541.400' },
    },
    groups: [
      {
        label: 'Compensation',
        questions: [
          makeQuestion(
            '541_400b',
            'This role is compensated on a salary or fee basis at or above $844 per week, or on an hourly basis at or above $27.63 per hour.',
            'The hourly alternative is unique to this exemption.',
            { cfr: { ref: '29 C.F.R. § 541.400(b)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-E/section-541.400' } },
          ),
        ],
      },
      {
        label: 'Duties',
        questions: [
          makeQuestion(
            '541_400a',
            'This role is employed as a computer systems analyst, programmer, software engineer, or similarly skilled worker in the computer field.',
            'Help desk support, data entry, and general hardware roles typically do not qualify.',
            { cfr: { ref: '29 C.F.R. § 541.400(a)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-E/section-541.400' } },
          ),
          makeQuestion(
            '541_400b1_4',
            "This role's primary duty consists of systems analysis, design or development of computer systems or programs, or modification of operating system programs, or a combination requiring the same level of skill.",
            null,
            { cfr: { ref: '29 C.F.R. § 541.400(b)(1)–(4)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-E/section-541.400' } },
          ),
        ],
      },
    ],
  },
  {
    id: 'outside-sales',
    title: 'Outside Sales',
    isExemption: true,
    description: 'Both statements must be true. There is no salary requirement for this exemption.',
    citation: {
      flsa: '§ 13(a)(1)',
      fact_sheet: { ref: '17F', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17f-overtime-outside-sales' },
      cfr: { ref: '29 C.F.R. § 541.500', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-F/section-541.500' },
    },
    groups: [
      {
        label: null,
        questions: [
          makeQuestion(
            '541_500a1',
            "This role's primary duty is making sales or obtaining orders or contracts for services or facilities for which a consideration will be paid.",
            null,
            { cfr: { ref: '29 C.F.R. § 541.500(a)(1)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-F/section-541.500' } },
          ),
          makeQuestion(
            '541_500a2',
            "This role is customarily and regularly engaged away from the employer's place of business.",
            "Inside sales roles, or those who primarily sell by phone or remotely from an employer's location, do not meet this criterion.",
            { cfr: { ref: '29 C.F.R. § 541.500(a)(2)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-F/section-541.500' } },
          ),
        ],
      },
    ],
  },
  {
    id: 'highly-compensated',
    title: 'Highly Compensated',
    isExemption: true,
    description:
      'All three statements must be checked. This is an alternative path that does not require satisfying the full duties tests of other categories.',
    citation: {
      flsa: '§ 13(a)(1)',
      fact_sheet: { ref: '17H', url: 'https://www.dol.gov/agencies/whd/fact-sheets/17h-overtime-highly-compensated' },
      cfr: { ref: '29 C.F.R. § 541.601', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.601' },
    },
    groups: [
      {
        label: 'Compensation',
        questions: [
          makeQuestion(
            '541_601b',
            "This role's total annual compensation includes at least $844 per week paid on a salary or fee basis.",
            'Required even where total compensation well exceeds the $132,964 threshold',
            { cfr: { ref: '29 C.F.R. § 541.601(b)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.601' } },
          ),
          makeQuestion(
            '541_601a',
            "This role's total annual compensation is at or above $132,964.",
            'May include commissions and nondiscretionary bonuses but not board, lodging, or other facilities.',
            { cfr: { ref: '29 C.F.R. § 541.601(a)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.601' } },
          ),
        ],
      },
      {
        label: 'Duties',
        questions: [
          makeQuestion(
            '541_601d',
            'This role performs office or non-manual work and customarily and regularly performs at least one of the duties of an exempt executive, administrative, or professional employee.',
            'Only one qualifying duty from any of the three categories is required. The full duties tests do not apply here.',
            { cfr: { ref: '29 C.F.R. § 541.601(a) and (d)', url: 'https://www.ecfr.gov/on/2024-08-01/title-29/subtitle-B/chapter-V/subchapter-A/part-541/subpart-G/section-541.601' } },
          ),
        ],
      },
    ],
  },
];

export const FEDERAL_FLSA_2026 = {
  id: 'federal-2026',
  title: 'Federal FLSA Form',
  sections: FLSA_SECTIONS,
};

export const DEFAULT_FLSA_CONFIG = FEDERAL_FLSA_2026;
